/**
 * ProductCard — sidebar product item with editing controls.
 * Replaces window.ProductCard. Matches original PHP-version design.
 */
import { useState, useEffect, useRef, type FC } from 'react';
import { Icon } from './Icon';
import { apiPostForm, apiPost } from '../services/api';
import type { Product } from '../stores/appStore';

interface Props {
  product: Product;
  index: number;
  totalProducts: number;
  allProducts: Product[];
  onToggle: (id: string) => void;
  onUpdate: (id: string, updates: Partial<Product>) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: 'up' | 'down') => void;
  onDuplicate: (product: Product) => void;
  activeTab: string;
}

export const ProductCard: FC<Props> = ({
  product,
  index,
  totalProducts,
  allProducts,
  onToggle,
  onUpdate,
  onDelete,
  onMove,
  onDuplicate,
  activeTab,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localName, setLocalName] = useState(product.name);
  const [isEditingResolution, setIsEditingResolution] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const resolutionContainerRef = useRef<HTMLDivElement>(null);

  const configTab = activeTab === 'products' ? 'product' : 'mockup';

  const currentWidth = configTab === 'mockup' ? (product.mockupWidth || product.width) : product.width;
  const currentHeight = configTab === 'mockup' ? (product.mockupHeight || product.height) : product.height;
  const currentMask = configTab === 'mockup' ? product.mockupMask : product.mask;
  const currentOverlay = configTab === 'mockup' ? product.mockupOverlay : product.overlay;

  const [localWidth, setLocalWidth] = useState(currentWidth);
  const [localHeight, setLocalHeight] = useState(currentHeight);

  useEffect(() => {
    setLocalName(product.name);
    setLocalWidth(currentWidth);
    setLocalHeight(currentHeight);
  }, [product.id, configTab, product.width, product.height, product.mockupWidth, product.mockupHeight, product.name, currentWidth, currentHeight]);

  const handleResolutionUpdate = () => {
    setIsEditingResolution(false);
    const updates: Partial<Product> = {};
    if (configTab === 'mockup') {
      updates.mockupWidth = localWidth;
      updates.mockupHeight = localHeight;
    } else {
      updates.width = localWidth;
      updates.height = localHeight;
    }
    onUpdate(product.id, updates);
  };

  useEffect(() => {
    if (!isEditingResolution) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (resolutionContainerRef.current && !resolutionContainerRef.current.contains(e.target as Node)) {
        handleResolutionUpdate();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditingResolution, localWidth, localHeight, product.id, configTab]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'mask' | 'overlay') => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('files', file);
    fd.append('type', 'asset');
    fd.append('assetType', type);
    try {
      const data = await apiPostForm<{ success: boolean; files?: { url: string }[]; message?: string }>(
        '/api/files/upload',
        fd,
      );
      if (data.success && data.files?.[0]?.url) {
        const fieldName = configTab === 'mockup'
          ? (type === 'mask' ? 'mockupMask' : 'mockupOverlay')
          : type;
        onUpdate(product.id, { [fieldName]: data.files[0].url });
      } else {
        alert('Ошибка загрузки: ' + (data.message || 'Unknown'));
      }
    } catch (err) {
      console.error(err);
    } finally {
      e.target.value = '';
    }
  };

  const handleFileDelete = async (type: 'mask' | 'overlay', fileUrl: string) => {
    if (!fileUrl) return;
    const fieldName = configTab === 'mockup'
      ? (type === 'mask' ? 'mockupMask' : 'mockupOverlay')
      : type;
    onUpdate(product.id, { [fieldName]: '' });

    if (!confirm('Удалить файл с сервера? Отмените, если он нужен для других мокапов.')) return;

    const isUsedElsewhere = allProducts?.some((op) => {
      if (op.id === product.id) return false;
      return op.mask === fileUrl || op.overlay === fileUrl || op.mockupMask === fileUrl || op.mockupOverlay === fileUrl;
    });
    if (isUsedElsewhere) return;

    try {
      const filename = fileUrl.split('/').pop();
      await apiPost('/api/files/delete', { filename, isAsset: true });
    } catch (err) {
      console.error('Ошибка удаления файла:', err);
    }
  };

  return (
    <div className={`product-card group ${product.enabled ? 'product-card-enabled' : 'product-card-disabled'}`}>
      {/* --- HEAD --- */}
      <div className="p-3 flex items-center gap-3 border-b border-slate-700/50">
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(product.id); }}
          className={`checkbox ${product.enabled ? 'checkbox-checked' : 'checkbox-unchecked'}`}
        >
          {product.enabled && <span><Icon name="check" className="w-3.5 h-3.5" /></span>}
        </button>

        <div className="flex-1 min-w-0">
          {isEditing ? (
            <input
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onBlur={() => { setIsEditing(false); onUpdate(product.id, { name: localName }); }}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
              autoFocus
              className="w-full bg-slate-900 border border-indigo-500 rounded px-1.5 py-0.5 text-sm text-white outline-none"
            />
          ) : (
            <div className="flex items-center gap-2 group/name cursor-pointer" onClick={() => setIsEditing(true)}>
              <span className="text-sm font-medium text-slate-200 truncate">{product.name}</span>
              <Icon name="pencil" className="w-3 h-3 text-slate-600 hover-opacity-show" />
            </div>
          )}
          <div className="text-[10px] text-slate-500 font-mono mt-0.5">
            {currentWidth}x{currentHeight}px ({configTab === 'mockup' ? 'Мокап' : 'Товар'})
          </div>
        </div>

        <button
          onClick={() => setShowSettings(s => !s)}
          className={`btn-icon transition-colors ${
            showSettings ? 'text-indigo-400 bg-indigo-500/20' : 'text-slate-500 hover:text-slate-300'
          }`}
          title="Настройки"
        >
          <Icon name={showSettings ? 'chevron-up' : 'sliders-horizontal'} className="w-4 h-4" />
        </button>

        <button
          onClick={() => onDelete(product.id)}
          className="btn-icon btn-danger hover-opacity-show"
          title="Удалить"
        >
          <Icon name="trash-2" className="w-4 h-4" />
        </button>
      </div>

      {/* --- BODY (Settings) --- */}
      {product.enabled && (
        <div
          ref={settingsRef}
          className="product-settings-body bg-slate-900/20"
          style={{
            maxHeight: showSettings ? (settingsRef.current ? settingsRef.current.scrollHeight + 'px' : '500px') : '0',
            opacity: showSettings ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease',
            padding: showSettings ? undefined : '0 12px',
          }}
        >
          <div className="p-3 space-y-3">
            {/* Resolution */}
            <div ref={resolutionContainerRef} className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5">
              <span className="text-[10px] text-slate-500 uppercase font-bold shrink-0">Размер</span>
              <input
                type="number"
                value={localWidth}
                onChange={(e) => setLocalWidth(parseInt(e.target.value) || 1)}
                onFocus={() => setIsEditingResolution(true)}
                onBlur={handleResolutionUpdate}
                onKeyDown={(e) => { if (e.key === 'Enter') handleResolutionUpdate(); }}
                className="w-14 bg-slate-900 border border-slate-600 focus:border-indigo-500 rounded px-1.5 py-0.5 text-[11px] text-white outline-none font-mono text-center"
              />
              <span className="text-slate-500 text-[10px]">x</span>
              <input
                type="number"
                value={localHeight}
                onChange={(e) => setLocalHeight(parseInt(e.target.value) || 1)}
                onFocus={() => setIsEditingResolution(true)}
                onBlur={handleResolutionUpdate}
                onKeyDown={(e) => { if (e.key === 'Enter') handleResolutionUpdate(); }}
                className="w-14 bg-slate-900 border border-slate-600 focus:border-indigo-500 rounded px-1.5 py-0.5 text-[11px] text-white outline-none font-mono text-center"
              />
              <span className="text-[10px] text-slate-500">px</span>
            </div>

            {/* Move / Duplicate / Prefix */}
            <div className="flex items-center gap-2">
              <div className="flex bg-slate-800 rounded-md border border-slate-700 p-0.5">
                <button
                  disabled={index === 0}
                  onClick={() => onMove(index, 'up')}
                  className="btn-icon text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:text-slate-400"
                >
                  <Icon name="arrow-up" className="w-3.5 h-3.5" />
                </button>
                <div className="w-px bg-slate-700 my-1"></div>
                <button
                  disabled={index === totalProducts - 1}
                  onClick={() => onMove(index, 'down')}
                  className="btn-icon text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:text-slate-400"
                >
                  <Icon name="arrow-down" className="w-3.5 h-3.5" />
                </button>
              </div>

              <button
                onClick={() => onDuplicate(product)}
                className="btn-secondary p-1.5 px-2 rounded-md text-xs flex items-center gap-1.5"
              >
                <Icon name="copy" className="w-3.5 h-3.5" />
                <span>Дубль</span>
              </button>

              {/* Prefix */}
              <div className="flex-1 flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-md px-2 py-1">
                <span className="text-[10px] text-slate-500 uppercase font-bold">Префикс</span>
                <input
                  type="text"
                  value={product.defaultPrefix || ''}
                  onChange={(e) => onUpdate(product.id, { defaultPrefix: e.target.value })}
                  className="w-full bg-transparent text-xs text-white outline-none font-mono"
                  placeholder="SKU"
                />
              </div>
            </div>

            {/* Rotation Control & Blend Mode */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const current = product.renderRotation || 0;
                  let next = 0;
                  if (current === 0) next = 90;
                  else if (current === 90) next = -90;
                  else next = 0;
                  onUpdate(product.id, { renderRotation: next });
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                  product.renderRotation
                    ? 'bg-indigo-500/20 border-indigo-500 text-indigo-300'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white'
                }`}
                title="Поворот после рендера"
              >
                <Icon name="rotate-cw" className="w-3.5 h-3.5" />
                <span>
                  {product.renderRotation === 90 ? '+90°' : product.renderRotation === -90 ? '-90°' : '0°'}
                </span>
              </button>

              <select
                value={product.blendMode || 'source-over'}
                onChange={(e) => onUpdate(product.id, { blendMode: e.target.value })}
                className="bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-md px-2 py-1.5 outline-none hover:border-slate-600 focus:border-indigo-500 transition-colors cursor-pointer"
                title="Режим наложения"
              >
                <option value="source-over">Нормальный</option>
                <option value="multiply">Умножение</option>
                <option value="screen">Экран</option>
                <option value="overlay">Перекрытие</option>
                <option value="darken">Затемнение</option>
                <option value="lighten">Осветление</option>
                <option value="color-dodge">Осветл. основы</option>
                <option value="color-burn">Затемн. основы</option>
                <option value="hard-light">Жесткий свет</option>
                <option value="soft-light">Мягкий свет</option>
              </select>
            </div>

            {/* DPI */}
            <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-md px-2 py-1">
              <span className="text-[10px] text-slate-500 uppercase font-bold shrink-0">DPI</span>
              <input
                type="number"
                value={product.dpi || 300}
                onChange={(e) => onUpdate(product.id, { dpi: parseInt(e.target.value) || 300 })}
                className="w-16 bg-transparent text-xs text-white outline-none font-mono text-center"
              />
            </div>

            {/* Files (Mask & Overlay) */}
            <div className="grid grid-cols-2 gap-2">
              {/* Mask */}
              <div className="relative">
                <label className={`file-upload-button ${currentMask ? 'file-upload-button-active' : 'file-upload-button-inactive'}`}>
                  <div className="flex items-center gap-1.5 text-xs font-medium">
                    <Icon name="layers" className={`w-3.5 h-3.5 ${currentMask ? 'text-indigo-400' : 'text-slate-400'}`} />
                    <span className={currentMask ? 'text-indigo-300' : 'text-slate-400'}>{configTab === 'mockup' ? 'М. Маска' : 'Маска'}</span>
                  </div>
                  <input type="file" className="hidden" accept="image/png" onChange={e => handleFileUpload(e, 'mask')} />
                </label>
                {currentMask && (
                  <button onClick={() => handleFileDelete('mask', currentMask)} className="delete-button">
                    <Icon name="x" className="w-3 h-3" />
                  </button>
                )}
              </div>

              {/* Overlay */}
              <div className="relative">
                <label className={`file-upload-button ${currentOverlay ? 'file-upload-button-active' : 'file-upload-button-inactive'}`}>
                  <div className="flex items-center gap-1.5 text-xs font-medium">
                    <Icon name="eye" className={`w-3.5 h-3.5 ${currentOverlay ? 'text-indigo-400' : 'text-slate-400'}`} />
                    <span className={currentOverlay ? 'text-indigo-300' : 'text-slate-400'}>{configTab === 'mockup' ? 'М. Оверлей' : 'Оверлей'}</span>
                  </div>
                  <input type="file" className="hidden" accept="image/png" onChange={e => handleFileUpload(e, 'overlay')} />
                </label>
                {currentOverlay && (
                  <button onClick={() => handleFileDelete('overlay', currentOverlay)} className="delete-button">
                    <Icon name="x" className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Linked Mockups */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-md p-2 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Icon name="link" className="w-3 h-3 text-slate-500" />
                <span className="text-[10px] text-slate-500 uppercase font-bold">Связать мокапы</span>
                {(product.linkedMockups?.length || 0) > 0 && (
                  <span className="text-[9px] bg-indigo-500/20 text-indigo-300 px-1.5 rounded-full ml-auto">
                    {product.linkedMockups!.length}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1">
                {allProducts
                  .filter((p) => p.id !== product.id && p.tab === product.tab)
                  .map((other) => {
                    const isLinked = product.linkedMockups?.includes(other.id);
                    return (
                      <button
                        key={other.id}
                        onClick={() => {
                          const current = product.linkedMockups || [];
                          let next: string[];
                          if (isLinked) {
                            next = current.filter((lid) => lid !== other.id);
                            // Also remove the back-link
                            const otherLinks = (other.linkedMockups || []).filter((lid) => lid !== product.id);
                            onUpdate(other.id, { linkedMockups: otherLinks });
                          } else {
                            next = [...current, other.id];
                            // Also add back-link
                            const otherLinks = [...(other.linkedMockups || []), product.id];
                            onUpdate(other.id, { linkedMockups: otherLinks });
                          }
                          onUpdate(product.id, { linkedMockups: next });
                        }}
                        className={`text-[9px] px-2 py-0.5 rounded-md border transition-all truncate max-w-[80px] ${
                          isLinked
                            ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300'
                            : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-600'
                        }`}
                        title={isLinked ? `Отвязать ${other.name}` : `Связать с ${other.name}`}
                      >
                        {isLinked && <span className="mr-0.5">✓</span>}
                        {other.name}
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* Copy / Delete actions */}
            <div className="flex gap-2 pt-1 border-t border-slate-700/30">
              <button
                onClick={() => onDuplicate(product)}
                className="btn-secondary flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium"
              >
                <Icon name="copy" className="w-3 h-3" /> Копировать
              </button>
              <button
                onClick={() => onDelete(product.id)}
                className="btn-danger flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium"
              >
                <Icon name="trash-2" className="w-3 h-3" /> Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
